/**
 * UUID v4 생성 — insecure context 안전판.
 *
 * crypto.randomUUID는 secure context(https·localhost) 전용이라, 사내망
 * http 배포(예: http://10.254.x.x)에서는 존재하지 않아 "crypto.randomUUID
 * is not a function"으로 터진다(실측: 예제 프로젝트 설치 실패). 모든 UUID
 * 생성은 이 헬퍼를 경유한다 — crypto.getRandomValues는 insecure context에서도
 * 동작하므로 폴백도 암호학적 난수를 유지한다.
 */

export function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // crypto 자체가 없는 런타임은 명시적으로 거부 — Math.random 강등(충돌 위험)
  // 대신 환경 문제를 즉시 드러낸다. 대상 런타임(브라우저·Node 18+)에는 항상 있다.
  if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
    throw new TypeError('crypto.getRandomValues를 지원하지 않는 환경입니다 — UUID를 생성할 수 없습니다');
  }

  // RFC 4122 v4 — getRandomValues 기반 (Math.random 금지: 충돌 위험)
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx

  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * 접두어를 붙인 불투명 id — uuid v4 전체(하이픈 없이 32자). 자르지 않는다: 앞 8자(32비트)로 자르면 덱 커밋 한 번에
 * 수백 개를 짓는 노드 id 가 모델 하나 안에서도 겹칠 수 있다(n개면 약 n²/2³³). 실행 id 를 잘랐다가 지난 실행·다른 복제본의
 * id 와 겹친 것과 같은 실패다(SM #243).
 */
export function generateOpaqueId(prefix: string): string {
  return `${prefix}${generateUuid().replace(/-/g, '')}`;
}
